import { useEffect, useRef, useCallback } from "react";
import { useQueryClient, QueryKey } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CHATS_ROOT_KEY, channelMessagesKey } from "@/hooks/useChat";
import { TICKETS_ROOT_KEY, ticketDetailKey } from "@/hooks/useSupportTickets";
import { useAuth } from "@/contexts/AuthContext";
import {
  useActiveConversationStore,
  type ActiveConversation,
} from "@/stores/useActiveConversationStore";
import { resolveChatNotificationTitle } from "@/utils/chatNotificationLabel";
import { routes } from "@/routes";
import { isTabFocused } from "@/utils/tabFocus";

type AddMessageListener = (listener: (message: unknown) => void) => () => void;

/** Max characters shown in a toast preview before ellipsis. */
const PREVIEW_MAX = 120;

function truncate(text: string, max = PREVIEW_MAX): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Where a chat notification's "Open" action should navigate. A `ticketId` sends
 * the user to the support-ticket route; otherwise to the channel chat.
 */
export function chatNotificationActionUrl(params: {
  channelId: string;
  ticketId: string | undefined;
}): string {
  return params.ticketId
    ? routes.supportTicket(params.ticketId)
    : `/chat/${params.channelId}`;
}

/** Is the user currently viewing the conversation this event belongs to? */
export function isViewingConversation(
  active: ActiveConversation | null,
  target: { channelId: string; ticketId: string | undefined },
): boolean {
  if (!active) return false;
  return target.ticketId
    ? active.ticketId === target.ticketId
    : active.channelId === target.channelId;
}

/**
 * Pure toast-suppression decision (extracted so it can be unit-tested without a
 * live socket). We DO toast a new inbound message unless:
 *  - it's not a create (edit / delete never toast),
 *  - it has no stable message id (can't dedupe re-delivery),
 *  - it's the current user's OWN message, or
 *  - the user is already viewing that conversation AND the tab is focused.
 */
export function shouldToastChatMessage(params: {
  isCreate: boolean;
  messageId: string | undefined;
  authorId: string | null | undefined;
  currentUserId: string | null;
  isViewingThisConversation: boolean;
  tabFocused: boolean;
}): boolean {
  const {
    isCreate,
    messageId,
    authorId,
    currentUserId,
    isViewingThisConversation,
    tabFocused,
  } = params;
  if (!isCreate) return false;
  if (!messageId) return false;
  if (authorId && currentUserId && authorId === currentUserId) return false;
  if (isViewingThisConversation && tabFocused) return false;
  return true;
}

/**
 * `eventData` shape published by api-alpha's `publishChatMessageEvent`
 * (`chat.message.{create|edit|delete}`, v1.0 envelope). Keys are camelCase and
 * — crucially — there is NO `author_name` on the wire.
 */
interface ChatMessageEventData {
  eventKind?: "create" | "edit" | "delete";
  channelId?: string;
  messageId?: string;
  authorId?: string | null;
  content?: string | null;
  /** Present only when the message originates from a support-ticket route. */
  ticketId?: string | null;
  /**
   * Server-trust signal for whether THIS recipient is a "notify target" of the
   * message. `undefined` or `true` mean "show the toast as normal" (this is
   * every event on today's wire shape, and every notify-target recipient
   * going forward). `false` means the server redacted this copy for this
   * recipient (e.g. an operator on the channel purely via team membership,
   * not a notify target) — never toast it, even though invalidation still
   * runs so the canonical thread still refetches.
   */
  notify?: boolean;
}

/** A `chat.message.*` WebSocket event as it reaches the shared listener. */
interface ChatMessageEvent {
  eventType: string;
  eventData?: ChatMessageEventData;
  data?: ChatMessageEventData;
}

function isChatMessageEvent(message: unknown): message is ChatMessageEvent {
  return (
    typeof message === "object" &&
    message !== null &&
    typeof (message as { eventType?: unknown }).eventType === "string" &&
    (message as { eventType: string }).eventType.startsWith("chat.message.")
  );
}

/**
 * `eventData` shape published by api-alpha's `publishTicketLifecycleEvent`
 * (`support.ticket.updated`, v1.0 envelope). Fired when a support ticket is
 * closed or reopened — carries NO message (close/reopen post no chat message), so
 * the `chat.message.*` path never sees it. `lifecycle` is the ticket's status
 * AFTER the transition.
 */
interface TicketLifecycleEventData {
  ticketId?: string;
  lifecycle?: "open" | "closed";
  channelId?: string;
}

interface TicketLifecycleEvent {
  eventType: string;
  eventData?: TicketLifecycleEventData;
  data?: TicketLifecycleEventData;
}

function isTicketLifecycleEvent(
  message: unknown,
): message is TicketLifecycleEvent {
  return (
    typeof message === "object" &&
    message !== null &&
    (message as { eventType?: unknown }).eventType === "support.ticket.updated"
  );
}

/**
 * Collapse a burst of message events (e.g. a rapid back-and-forth) into a
 * single invalidation pass per key.
 */
const INVALIDATION_DEBOUNCE_MS = 300;

/**
 * Live-append for the chat page and support ticket threads over the SHARED
 * WebSocket connection (`useWebSocketContext().addMessageListener`). No second
 * socket is opened — this simply registers another listener on the same
 * connection the notification / execution / asset consumers already use.
 *
 * On a `chat.message.*` event we do a TARGETED REFETCH (invalidate) rather than
 * a raw cache-append, for two reasons:
 *
 *  1. Branding. The WS payload carries NO `author_name`; the customer-facing
 *     "Studio1 Support" branding is applied server-side in the GET responses.
 *     Refetching the thread renders the message through that same branded
 *     `author_name`, so an operator's real name can never leak onto the
 *     customer surface. A raw cache-append would have no name to show.
 *
 *  2. Dedupe. The server thread is canonical, so the sender's own optimistic /
 *     echoed message (it receives its own event too) simply re-reads the same
 *     list keyed by message id — no duplicate row is ever inserted.
 *
 * `invalidateQueries` refetches only ACTIVE queries by default, so the open
 * conversation updates instantly while inactive threads are just marked stale
 * (refreshed on next mount / focus). The list keys are invalidated too so chat
 * recency + unread badges stay current.
 *
 * Polling in `useChat` / `useSupportTickets` remains as a fallback for when the
 * socket is briefly down.
 *
 * Must be mounted alongside the other shared-socket consumers (see App.tsx
 * `WebSocketEventConsumers`).
 */
export function useChatRealtime(
  addMessageListener: AddMessageListener | undefined,
) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Keep the latest user id / navigate in refs so the WS listener effect below
  // (which we deliberately do NOT re-subscribe on every render) always reads
  // fresh values without racing a second listener registration.
  const currentUserIdRef = useRef<string | null>(user?.id ?? null);
  const navigateRef = useRef(navigate);
  useEffect(() => {
    currentUserIdRef.current = user?.id ?? null;
  }, [user?.id]);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Pending invalidation keys, serialized + deduplicated across the debounce
  // window.
  const pendingInvalidations = useRef<Set<string>>(new Set());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushInvalidations = useCallback(() => {
    if (pendingInvalidations.current.size === 0) return;

    const keys = Array.from(pendingInvalidations.current);
    pendingInvalidations.current.clear();

    keys.forEach((serializedKey) => {
      const queryKey: QueryKey = JSON.parse(serializedKey);
      queryClient.invalidateQueries({ queryKey });
    });
  }, [queryClient]);

  const scheduleInvalidation = useCallback(
    (queryKey: QueryKey) => {
      pendingInvalidations.current.add(JSON.stringify(queryKey));

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(
        flushInvalidations,
        INVALIDATION_DEBOUNCE_MS,
      );
    },
    [flushInvalidations],
  );

  // Flush anything pending on unmount so a trailing event isn't dropped.
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        flushInvalidations();
      }
    };
  }, [flushInvalidations]);

  /**
   * Surface a new chat message as a transient sonner toast. This is ADDITIVE —
   * the query invalidation above still drives the live-append + unread badges,
   * and the DURABLE bell row now arrives independently as a server
   * `notification.created` event (type `chat_message`) consumed by
   * `useNotificationStore`. Resilient to missing fields.
   *
   * Suppression (toast-only — the durable bell row is unaffected):
   *  - Never toast about your OWN message (`authorId === currentUserId`).
   *  - Skip the toast when you are already viewing that conversation AND the tab
   *    is focused (you're actively reading it).
   */
  const dispatchChatNotification = useCallback(
    (params: {
      messageId: string | undefined;
      channelId: string;
      ticketId: string | undefined;
      authorId: string | null | undefined;
      content: string | null | undefined;
      isCreate: boolean;
    }) => {
      const { messageId, channelId, ticketId, authorId, content, isCreate } =
        params;

      // Toast — suppressed for own messages, edits/deletes, and while actively
      // viewing this conversation. The durable bell row arrives separately via
      // `notification.created`.
      const active = useActiveConversationStore.getState().activeConversation;
      const shouldToast = shouldToastChatMessage({
        isCreate,
        messageId,
        authorId,
        currentUserId: currentUserIdRef.current,
        isViewingThisConversation: isViewingConversation(active, {
          channelId,
          ticketId,
        }),
        tabFocused: isTabFocused(),
      });
      if (!shouldToast) return;

      // Label: support is always branded "Studio1 Support" (the WS carries no
      // author name, and we must never leak an operator's real name). For other
      // kinds, use the channel's DB-derived `display_name` from the chats-list
      // cache (for a DM that's the other participant's name).
      const title = resolveChatNotificationTitle({
        queryClient,
        channelId,
        ticketId,
      });
      const preview = truncate(content ?? "");
      const actionUrl = chatNotificationActionUrl({ channelId, ticketId });

      toast(title, {
        // Stable id so a re-delivered event for the same message collapses onto
        // the existing toast instead of double-toasting.
        id: `chat-msg-${messageId}`,
        description: preview || undefined,
        action: {
          label: "Open",
          onClick: () => navigateRef.current(actionUrl),
        },
      });
    },
    [queryClient],
  );

  useEffect(() => {
    if (!addMessageListener) return;

    const removeListener = addMessageListener((message) => {
      // Support ticket close/reopen: no chat message is posted, so this arrives as
      // its own `support.ticket.updated` event. Refetch the ticket detail (its
      // status flips open<->closed) + the ticket list (open/closed filtering) so
      // the open ticket view updates live without a manual refresh.
      if (isTicketLifecycleEvent(message)) {
        const lifecycleData = message.eventData ?? message.data;
        const lifecycleTicketId = lifecycleData?.ticketId;
        if (lifecycleTicketId) {
          scheduleInvalidation(ticketDetailKey(lifecycleTicketId));
          scheduleInvalidation(TICKETS_ROOT_KEY as unknown as QueryKey);
        }
        return;
      }

      if (!isChatMessageEvent(message)) return;

      const data = message.eventData ?? message.data;
      const channelId = data?.channelId;
      if (!channelId) return;

      // Refresh the open conversation + the chat list (recency + unread).
      scheduleInvalidation(channelMessagesKey(channelId));
      scheduleInvalidation(CHATS_ROOT_KEY as unknown as QueryKey);

      // Support tickets are keyed by ticket id (their thread rides the ticket
      // detail query), so refresh those too when the event names a ticket.
      const ticketId = data?.ticketId ?? undefined;
      if (ticketId) {
        scheduleInvalidation(ticketDetailKey(ticketId));
        scheduleInvalidation(TICKETS_ROOT_KEY as unknown as QueryKey);
      }

      // Additive: surface a toast + bell entry for new messages. `eventKind`
      // rides in the payload; fall back to the `chat.message.create` topic.
      const isCreate =
        data?.eventKind === "create" ||
        message.eventType === "chat.message.create";

      // Server-trust signal: this recipient was redacted out of this event
      // (not a notify target). Invalidation above has already run — this only
      // guards the toast.
      if (data?.notify === false) return;

      dispatchChatNotification({
        messageId: data?.messageId,
        channelId,
        ticketId,
        authorId: data?.authorId,
        content: data?.content,
        isCreate,
      });
    });

    return removeListener;
  }, [addMessageListener, scheduleInvalidation, dispatchChatNotification]);
}
